import { useEffect, useState } from "react";
import { Table, Spinner, Button, Badge } from "reactstrap";
import { saveAs } from "file-saver";
import { getInsuranceReport, exportInsuranceReportExcel } from "../TenantForm/Helpers/api";

const BUCKET = {
  EXPIRED: { row: "table-danger", label: "Expired", color: "danger" },
  EXPIRING: { row: "table-warning", label: "Expiring", color: "warning" },
  VALID: { row: "table-success", label: "Valid", color: "success" },
  NO_DATE: { row: "", label: "No date", color: "secondary" },
};

const DAY_PRESETS = [30, 60, 90];

const InsuranceTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [days, setDays] = useState(30);
  const [customDays, setCustomDays] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!mastersheetId) return undefined;
    setLoading(true);
    setError(null);
    getInsuranceReport(mastersheetId, days)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load report");
        setReport(resp.data);
      })
      .catch((err) => active && setError(err.message || "Failed to load insurance report"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [mastersheetId, days]);

  const applyCustom = () => {
    const n = parseInt(customDays, 10);
    if (!isNaN(n) && n > 0) setDays(n);
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportInsuranceReportExcel(mastersheetId, days);
      saveAs(new Blob([blob]), `Insurance_${masterSheetData?.building_name || "sheet"}.xlsx`);
    } catch (err) {
      console.error("Insurance excel export failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="text-center p-4"><Spinner color="primary" /> Loading insurance report…</div>;
  if (error) return <p className="text-danger p-3">{error}</p>;
  if (!report) return <p className="text-muted p-3">No data available.</p>;

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        {DAY_PRESETS.map((d) => (
          <Button key={d} size="sm" color={days === d ? "primary" : "outline-primary"} onClick={() => setDays(d)}>
            {d} days
          </Button>
        ))}
        <div className="d-flex align-items-center gap-1">
          <input
            type="number" min="1" className="form-control form-control-sm" style={{ width: 90 }}
            placeholder="Custom" value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyCustom()}
          />
          <Button size="sm" color="outline-primary" onClick={applyCustom}>Apply</Button>
        </div>
        <Button size="sm" color="success" className="ms-auto" onClick={handleExport} disabled={downloading}>
          {downloading ? <Spinner size="sm" /> : "Download Excel"}
        </Button>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Badge color="danger">Expired: {report.totals.expired}</Badge>
        <Badge color="warning" className="text-dark">Expiring ≤ {report.windowDays}d: {report.totals.expiring}</Badge>
        <Badge color="success">Valid: {report.totals.valid}</Badge>
        <Badge color="secondary">No date: {report.totals.noDate}</Badge>
        <Badge color="info" className="text-dark">Units: {report.totals.unitsTotal}</Badge>
      </div>

      <Table hover responsive className="table-sm">
        <thead className="table-light">
          <tr>
            <th>Unit #</th><th>Tenant</th><th>Insurance Expiry</th><th>Days Left</th><th>Status</th><th>Insurance Doc</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.map((r) => (
            <tr key={r.unitNo} className={BUCKET[r.bucket]?.row || ""}>
              <td><strong>{r.unitNo}</strong></td>
              <td>{r.tenant}</td>
              <td>{r.insuranceExpiry ? new Date(r.insuranceExpiry).toLocaleDateString("en-GB") : "—"}</td>
              <td>{r.daysUntilExpiry ?? "—"}</td>
              <td><Badge color={BUCKET[r.bucket]?.color || "secondary"}>{BUCKET[r.bucket]?.label || r.bucket}</Badge></td>
              <td>
                <Badge color={r.docStatus === "Uploaded" ? "success" : r.docStatus === "N/A" ? "secondary" : "danger"}>
                  {r.docStatus}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export { InsuranceTab };
